/** @param {string} apiKey */
const loadGoogleMaps = (apiKey) => {
  if (window.google?.maps) return Promise.resolve(window.google.maps);

  if (!window.__googleMapsLoadingPromise) {
    window.__googleMapsLoadingPromise = new Promise((resolve, reject) => {
      const callbackName = `initMapCallback_${Date.now()}`;
      const callbackRegistry = /** @type {Record<string, (() => void) | undefined>} */ (/** @type {unknown} */ (window));
      const cleanup = () => delete callbackRegistry[callbackName];

      callbackRegistry[callbackName] = () => {
        if (!window.google?.maps) {
          cleanup();
          reject(new Error('Google Maps loaded without the Maps JavaScript API'));
          return;
        }

        resolve(window.google.maps);
        cleanup();
      };

      const parameters = new URLSearchParams({
        key: apiKey,
        loading: 'async',
        callback: callbackName,
        v: 'weekly'
      });
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?${parameters}`;
      script.async = true;
      script.onerror = () => {
        cleanup();
        reject(new Error('Google Maps failed to load'));
      };
      document.head.appendChild(script);
    }).catch((error) => {
      console.error('[Map] Google Maps load error', error);
      window.__googleMapsLoadingPromise = undefined;
      throw error;
    });
  }

  return window.__googleMapsLoadingPromise;
};

class MapComponent extends HTMLElement {
  constructor() {
    super();
    this.handleResize = this.handleResize.bind(this);
    this.handleMapIntent = this.handleMapIntent.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.deferInitMap();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.removeEventListener('pointerenter', this.handleMapIntent);
    this.removeEventListener('focusin', this.handleMapIntent);
    this.removeEventListener('click', this.handleMapIntent);
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();

    if (this.resizeAnimationFrame) cancelAnimationFrame(this.resizeAnimationFrame);
  }

  deferInitMap() {
    this.addEventListener('pointerenter', this.handleMapIntent, { once: true });
    this.addEventListener('focusin', this.handleMapIntent, { once: true });
    this.addEventListener('click', this.handleMapIntent, { once: true });

    if (typeof window.IntersectionObserver !== 'function') {
      window.setTimeout(() => this.initMap(), 1500);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.intersectionObserver?.disconnect();
        this.initMap();
      },
      { rootMargin: '600px 0px' }
    );
    this.intersectionObserver.observe(this);
  }

  handleMapIntent() {
    this.intersectionObserver?.disconnect();
    this.initMap();
  }

  async initMap() {
    if (this.mapInitializing || this.map) return;
    this.mapInitializing = true;

    const apiKey = this.dataset.apiKey;
    const mapId = this.dataset.mapId;
    if (!apiKey) {
      console.error('[Map] Missing Google Maps API key');
      this.mapInitializing = false;
      return;
    }

    try {
      const maps = await loadGoogleMaps(apiKey);
      const markerLibrary = await maps.importLibrary('marker');
      const location = await this.resolveLocation(maps);

      if (!location) {
        console.error('[Map] No valid location provided');
        this.mapInitializing = false;
        return;
      }

      const zoom = Number(this.dataset.zoom) || 14;
      this.location = location;
      this.map = new maps.Map(this, {
        center: location,
        zoom,
        ...(mapId ? { mapId } : {}),
        disableDefaultUI: true,
        backgroundColor: 'transparent'
      });

      const markerTitle = this.dataset.markerTitle || this.dataset.address || '';

      if (mapId) {
        this.marker = new markerLibrary.AdvancedMarkerElement({
          position: location,
          map: this.map,
          title: markerTitle
        });
      } else {
        this.marker = new markerLibrary.Marker({
          position: location,
          map: this.map,
          title: markerTitle
        });
      }

      this.observeResize();
      this.mapInitializing = false;
    } catch (error) {
      console.error('[Map] Unable to initialize', error);
      this.mapInitializing = false;
    }
  }

  observeResize() {
    if (typeof window.ResizeObserver === 'function') {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this);
      return;
    }

    window.addEventListener('resize', this.handleResize);
  }

  handleResize() {
    if (!this.map || !this.location) return;
    if (this.resizeAnimationFrame) cancelAnimationFrame(this.resizeAnimationFrame);

    this.resizeAnimationFrame = requestAnimationFrame(() => {
      if (!this.location) return;
      this.map?.setCenter(this.location);
    });
  }

  /** @param {GoogleMapsApi} maps */
  async resolveLocation(maps) {
    const useLatLong = this.dataset.latlongCorrection === 'true';
    const latitude = this.dataset.lat?.trim() || '';
    const longitude = this.dataset.long?.trim() || '';

    if (useLatLong && latitude !== '' && longitude !== '') {
      const lat = Number(latitude);
      const lng = Number(longitude);
      const validLatitude = Number.isFinite(lat) && lat >= -90 && lat <= 90;
      const validLongitude = Number.isFinite(lng) && lng >= -180 && lng <= 180;

      if (validLatitude && validLongitude) return { lat, lng };
      console.warn('[Map] Invalid latitude or longitude; falling back to the map address');
    }

    const address = this.dataset.address?.trim();
    if (!address) return null;

    const geocoder = new maps.Geocoder();
    const response = await geocoder.geocode({ address });
    return response.results[0]?.geometry.location || null;
  }
}

if (!customElements.get('map-component')) {
  customElements.define('map-component', MapComponent);
}
